import { useState, useEffect, useMemo, useCallback } from 'react';
import { CalendarDays, List, Users, CheckCircle2, AlertTriangle, Loader2, Send, Copy, RefreshCw, X, Pencil, Eye } from 'lucide-react';
import PeakAvailabilityTab from './PeakAvailabilityTab';
import { peakCall, monthGrids, WEEK_HEAD, fmtNight, fmtLong, fmtStamp, pastDeadline } from '../utils/peakAvailability';

// ── Peak availability (admin) ─────────────────────────────────────────────────
// The office side of the sorters' "nights I can't work" map: publish the season to the
// portal, then read it back by night (list or calendar) and by sorter, with who hasn't
// answered yet. Backend: peak-availability-sb.js, settings KV `peakAvailability:<year>`.
// The same view, read-only (no set-up, no entering answers), is what the server's
// PEAK_VIEWERS see: Luis Rey in his sorter portal tab, Valeria on /sort-roster. The server
// decides — `readOnly` comes back with the data.

const card = { background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 14, padding: 20, marginBottom: 20 };
const label = { display: 'block', fontSize: '0.7rem', fontWeight: 700, color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 5 };
const input = { width: '100%', background: 'var(--hp-card, #1E1E24)', border: '1px solid var(--hp-border)', borderRadius: 8, color: 'var(--hp-text)', fontSize: '0.92rem', padding: '10px 12px', boxSizing: 'border-box', fontFamily: 'var(--font-body)' };
const btn = (bg) => ({ padding: '10px 16px', background: bg, color: 'var(--hp-black)', fontWeight: 800, border: 'none', borderRadius: 10, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8, fontFamily: 'var(--font-body)' });
const ghost = { ...btn('transparent'), color: 'var(--hp-dim)', border: '1px solid var(--hp-border)' };
const pill = (bg, fg) => ({ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.7rem', fontWeight: 700, padding: '3px 8px', borderRadius: 999, background: bg, color: fg });
const toggle = (on) => ({ padding: '8px 14px', borderRadius: 8, border: `1px solid ${on ? 'var(--hp-gold)' : 'var(--hp-border)'}`, background: on ? 'rgba(201,168,76,0.12)' : 'transparent', color: on ? 'var(--hp-gold)' : 'var(--hp-dim)', fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: 'var(--font-body)' });
const RED = 'var(--hp-red, #EF4444)', GREEN = 'var(--hp-green, #22C55E)', AMBER = 'var(--hp-amber, #F59E0B)', ROSE = '#E11D48';
const th = { textAlign: 'left', padding: '8px 10px', fontSize: '0.7rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: '1px solid var(--hp-border)' };
const td = { padding: '9px 10px', fontSize: '0.84rem', borderBottom: '1px solid var(--hp-border)', verticalAlign: 'top' };

export default function PeakAvailabilityView() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  const [view, setView] = useState('calendar');
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);
  const [pick, setPick] = useState('');                 // selected night (calendar)
  const [editFor, setEditFor] = useState(null);         // { login, name, cannot:Set, note }
  const [preview, setPreview] = useState(null);         // the nights for the sorter-view preview
  const flash = (msg, good = true) => { setToast({ msg, good }); setTimeout(() => setToast(null), 5000); };

  const load = useCallback(() => {
    setErr('');
    peakCall('getPeakAdmin').then((r) => {
      if (!r.success) { setErr(r.error || 'Could not load'); return; }
      setD(r);
      const s = r.survey, y = r.season;
      setForm({
        from: (s && s.from) || `${y}-11-15`,
        to: (s && s.to) || `${y}-12-24`,
        deadline: (s && s.deadline) || `${y}-11-02`,
        status: (s && s.status) || 'Open',
        message: (s && s.message) || r.defaultMessage,
      });
    }).catch(() => setErr('Could not reach the server'));
  }, []);
  useEffect(() => { load(); }, [load]);

  const survey = d && d.survey;
  const nights = useMemo(() => (survey && survey.nights) || [], [survey]);
  const grids = useMemo(() => monthGrids(nights), [nights]);
  const team = useMemo(() => (d && d.team) || [], [d]);
  const responses = useMemo(() => (d && d.responses) || {}, [d]);

  // Who answered, and per night who can't. Someone who answered but isn't on the active
  // team list (e.g. logged on behalf, since made inactive) still counts against the night.
  const people = useMemo(() => {
    const map = new Map(team.map((t) => [t.login, { ...t, answer: responses[t.login] || null }]));
    for (const [l, a] of Object.entries(responses)) if (!map.has(l)) map.set(l, { login: l, name: a.name || l, grade: '', answer: a, offTeam: true });
    return [...map.values()].sort((a, b) => (a.name || a.login).localeCompare(b.name || b.login));
  }, [team, responses]);
  const answered = people.filter((p) => p.answer);
  const waiting = people.filter((p) => !p.answer && !p.offTeam);
  const byNight = useMemo(() => {
    const m = new Map(nights.map((n) => [n.date, []]));
    for (const p of people) if (p.answer) for (const dt of p.answer.cannot) if (m.has(dt)) m.get(dt).push(p);
    return m;
  }, [nights, people]);
  const headcount = people.filter((p) => !p.offTeam).length;
  const freeOn = (date) => answered.length - (byNight.get(date) || []).length;

  async function publish() {
    setBusy(true);
    try {
      const r = await peakCall('setupPeak', { season: d.season, ...form });
      if (!r.success) throw new Error(r.error || 'Could not publish');
      flash(survey ? 'Updated on the sorters portal.' : 'Published. Sorters see it on their portal now.');
      load();
    } catch (e) { flash(e.message, false); } finally { setBusy(false); }
  }
  async function saveFor() {
    setBusy(true);
    try {
      const r = await peakCall('saveFor', { season: d.season, login: editFor.login, name: editFor.name, cannot: [...editFor.cannot], note: editFor.note });
      if (!r.success) throw new Error(r.error || 'Could not save');
      flash(`Saved for ${editFor.name || editFor.login}.`);
      setEditFor(null); load();
    } catch (e) { flash(e.message, false); } finally { setBusy(false); }
  }
  async function showPreview() {
    if (preview) { setPreview(null); return; }
    setBusy(true);
    try {
      const r = await peakCall('previewPeak', { season: d.season, ...form });
      if (!r.success) throw new Error(r.error || 'Could not build the preview');
      setPreview(r.survey);
    } catch (e) { flash(e.message, false); } finally { setBusy(false); }
  }
  function copyMessage() {
    const text = `${form.message}\n\nOpen the Night Sorters portal → Peak availability: ${window.location.origin}/sorters`;
    navigator.clipboard.writeText(text).then(() => flash('Message copied.'), () => flash('Could not copy', false));
  }

  if (err) return <div style={card}><span style={pill('rgba(239,68,68,0.12)', RED)}><AlertTriangle size={13} /> {err}</span></div>;
  if (!d || !form) return <div style={card}><Loader2 size={18} style={{ animation: 'spin 0.8s linear infinite' }} /></div>;

  const late = pastDeadline(survey, d.today);
  const satRuns = nights.filter((n) => n.peak).length;
  const op = d.operating || {};
  const pickNight = nights.find((n) => n.date === pick);
  const readOnly = !!d.readOnly;
  // The preview follows the message and deadline as they're typed; the nights are the ones
  // built when Preview was clicked (click again after changing the dates).
  const previewSurvey = preview && { ...preview, message: form.message, deadline: form.deadline };

  return (
    <>
      {toast && <div style={{ ...pill(toast.good ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)', toast.good ? GREEN : RED), padding: '9px 14px', marginBottom: 16 }}>{toast.good ? <CheckCircle2 size={14} /> : <AlertTriangle size={14} />} {toast.msg}</div>}

      {readOnly && (
        <div style={{ ...card, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <CalendarDays size={20} color="var(--hp-gold)" />
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.3rem', margin: 0 }}>Peak {d.season} availability</h2>
          {survey
            ? <span style={{ fontSize: '0.8rem', color: 'var(--hp-dim)' }}>{nights.length} nights · {satRuns} Saturday runs · due {fmtLong(survey.deadline)}</span>
            : <span style={pill('rgba(245,158,11,0.14)', AMBER)}>Not sent to the sorters yet</span>}
          <button onClick={load} style={{ ...ghost, marginLeft: 'auto', padding: '6px 12px', fontSize: '0.78rem' }}><RefreshCw size={13} /> Refresh</button>
        </div>
      )}

      {/* ── Season setup + the message ─────────────────────────────────────── */}
      {!readOnly && (<>
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
          <CalendarDays size={20} color="var(--hp-gold)" />
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.3rem', margin: 0 }}>Peak {d.season} availability</h2>
          {survey
            ? <span style={pill(survey.status === 'Open' ? 'rgba(34,197,94,0.12)' : 'rgba(148,163,184,0.16)', survey.status === 'Open' ? GREEN : 'var(--hp-dim)')}>{survey.status === 'Open' ? 'Live on portal' : 'Closed'}</span>
            : <span style={pill('rgba(245,158,11,0.14)', AMBER)}>Not published yet</span>}
          <button onClick={load} style={{ ...ghost, marginLeft: 'auto', padding: '6px 12px', fontSize: '0.78rem' }}><RefreshCw size={13} /> Refresh</button>
        </div>
        <p style={{ fontSize: '0.84rem', color: 'var(--hp-dim)', margin: '0 0 14px', lineHeight: 1.55 }}>
          Sorters tap only the nights they <strong>can't</strong> work. Nights follow the roster's operating nights (Sun–Thu, plus peak Fridays {op.peakFrom ? `${fmtNight(op.peakFrom)} – ${fmtNight(op.peakTo)}` : '(peak window not set)'} = the Saturday runs). A night is left out when the next day is a public holiday, because nothing is delivered that day.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 12 }}>
          <div><label style={label}>First night</label><input type="date" style={input} value={form.from} onChange={(e) => setForm({ ...form, from: e.target.value })} /></div>
          <div><label style={label}>Last night</label><input type="date" style={input} value={form.to} onChange={(e) => setForm({ ...form, to: e.target.value })} /></div>
          <div><label style={label}>Reply by</label><input type="date" style={input} value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })} /></div>
          <div><label style={label}>Portal</label>
            <select style={input} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
              <option value="Open">Open: sorters can answer</option>
              <option value="Closed">Closed: read-only</option>
            </select>
          </div>
        </div>
        <label style={label}>Message to sorters (shown at the top of their tab)</label>
        <textarea style={{ ...input, minHeight: 210, lineHeight: 1.5, resize: 'vertical' }} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
        <div style={{ display: 'flex', gap: 10, marginTop: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <button style={btn('var(--hp-gold)')} disabled={busy} onClick={publish}>{busy ? <Loader2 size={15} style={{ animation: 'spin 0.8s linear infinite' }} /> : <Send size={15} />} {survey ? 'Update on portal' : 'Publish to sorters portal'}</button>
          <button style={ghost} onClick={copyMessage}><Copy size={14} /> Copy message + link</button>
          <button style={preview ? { ...ghost, color: 'var(--hp-gold)', borderColor: 'var(--hp-gold)' } : ghost} disabled={busy} onClick={showPreview}><Eye size={14} /> {preview ? 'Close preview' : 'Preview what sorters see'}</button>
          <button style={ghost} onClick={() => setForm({ ...form, message: d.defaultMessage })}>Reset message</button>
          {survey && <span style={{ fontSize: '0.8rem', color: 'var(--hp-dim)' }}>{nights.length} nights · {satRuns} Saturday runs · due {fmtLong(survey.deadline)}</span>}
        </div>
      </div>

      {previewSurvey && (
        <div style={{ ...card, background: 'transparent', borderStyle: 'dashed' }}>
          <div style={{ maxWidth: 420, margin: '0 auto' }}>
            <PeakAvailabilityTab sorter={{ login: '', name: '' }} preview={previewSurvey} />
          </div>
        </div>
      )}
      </>)}

      {survey && (
        <>
          {/* ── Who's answered ─────────────────────────────────────────────── */}
          <div style={{ ...card, display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center', borderColor: waiting.length && late ? RED : 'var(--hp-border)' }}>
            <div style={{ fontSize: '1.6rem', fontWeight: 800 }}>{answered.filter((p) => !p.offTeam).length}<span style={{ fontSize: '1rem', color: 'var(--hp-dim)' }}> / {headcount}</span></div>
            <div style={{ flex: 1, minWidth: 220 }}>
              <div style={{ fontWeight: 800 }}>answered{late ? ', deadline passed' : ''}</div>
              <div style={{ fontSize: '0.8rem', color: 'var(--hp-dim)', marginTop: 3 }}>
                {waiting.length ? <>Still waiting on: {waiting.map((p) => p.name || p.login).join(', ')}</> : 'Everyone has answered.'}
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
            <button style={toggle(view === 'calendar')} onClick={() => setView('calendar')}><CalendarDays size={14} /> Calendar</button>
            <button style={toggle(view === 'list')} onClick={() => setView('list')}><List size={14} /> List by night</button>
            <button style={toggle(view === 'people')} onClick={() => setView('people')}><Users size={14} /> By sorter</button>
          </div>

          {view === 'calendar' && (
            <div style={card}>
              <p style={{ fontSize: '0.8rem', color: 'var(--hp-dim)', margin: '0 0 12px' }}>Each night shows how many of the {answered.length} who answered <strong style={{ color: GREEN }}>can</strong> work and how many <strong style={{ color: RED }}>can't</strong>. Click a night for the names.</p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 24 }}>
                {grids.map((g) => (
                  <div key={g.key}>
                    <div style={{ fontWeight: 800, marginBottom: 8 }}>{g.title}</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
                      {WEEK_HEAD.map((h) => <div key={h} style={{ fontSize: '0.66rem', fontWeight: 700, color: 'var(--hp-dim)', textAlign: 'center' }}>{h}</div>)}
                      {g.cells.map((c, i) => {
                        if (!c) return <div key={i} />;
                        const n = c.night;
                        if (!n) return <div key={i} style={{ minHeight: 54, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: 6, fontSize: '0.74rem', color: 'var(--hp-dim)', opacity: 0.35 }}>{c.day}</div>;
                        const no = (byNight.get(n.date) || []).length;
                        const sel = pick === n.date;
                        return (
                          <button key={i} onClick={() => setPick(sel ? '' : n.date)}
                            style={{ minHeight: 54, borderRadius: 8, border: `1.5px solid ${sel ? 'var(--hp-gold)' : n.peak ? ROSE : 'var(--hp-border)'}`, background: no ? `rgba(239,68,68,${Math.min(0.08 + no * 0.06, 0.4)})` : 'rgba(34,197,94,0.07)', color: 'var(--hp-text)', cursor: 'pointer', padding: '4px 2px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, fontFamily: 'var(--font-body)' }}>
                            <span style={{ fontWeight: 800, fontSize: '0.8rem' }}>{c.day}{n.peak && <span style={{ color: ROSE, fontSize: '0.56rem', marginLeft: 3 }}>SAT</span>}</span>
                            <span style={{ fontSize: '0.66rem', color: GREEN, fontWeight: 700 }}>{freeOn(n.date)} ✓</span>
                            {no > 0 && <span style={{ fontSize: '0.66rem', color: RED, fontWeight: 700 }}>{no} ✗</span>}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
              {pickNight && (
                <div style={{ marginTop: 18, padding: 14, borderRadius: 10, border: '1px solid var(--hp-gold)', background: 'rgba(201,168,76,0.06)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <strong>{fmtLong(pickNight.date)} night</strong>
                    {pickNight.peak && <span style={pill('rgba(225,29,72,0.14)', ROSE)}>Saturday run · {fmtNight(pickNight.delivery)}</span>}
                    <button onClick={() => setPick('')} style={{ ...ghost, marginLeft: 'auto', padding: '4px 8px' }}><X size={13} /></button>
                  </div>
                  <NightNames night={pickNight} cant={byNight.get(pickNight.date) || []} answered={answered} waiting={waiting} />
                </div>
              )}
            </div>
          )}

          {view === 'list' && (
            <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 560 }}>
                <thead><tr><th style={th}>Night</th><th style={th}>Can work</th><th style={th}>Can't work</th><th style={th}>Who can't</th></tr></thead>
                <tbody>
                  {nights.map((n) => {
                    const cant = byNight.get(n.date) || [];
                    return (
                      <tr key={n.date} style={{ background: n.peak ? 'rgba(225,29,72,0.05)' : 'transparent' }}>
                        <td style={{ ...td, whiteSpace: 'nowrap', fontWeight: 700 }}>{fmtNight(n.date)}{n.peak && <span style={{ ...pill('rgba(225,29,72,0.14)', ROSE), marginLeft: 8 }}>Sat run</span>}</td>
                        <td style={{ ...td, color: GREEN, fontWeight: 800 }}>{freeOn(n.date)}</td>
                        <td style={{ ...td, color: cant.length ? RED : 'var(--hp-dim)', fontWeight: 800 }}>{cant.length}</td>
                        <td style={{ ...td, color: 'var(--hp-dim)' }}>{cant.map((p) => p.name || p.login).join(', ') || '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {waiting.length > 0 && <p style={{ fontSize: '0.78rem', color: 'var(--hp-dim)', padding: '10px 14px', margin: 0 }}>Not in these counts: {waiting.length} who haven't answered.</p>}
            </div>
          )}

          {view === 'people' && (
            <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
                <thead><tr><th style={th}>Sorter</th><th style={th}>Answer</th><th style={th}>Nights they can't work</th><th style={th}>Note</th>{!readOnly && <th style={th}></th>}</tr></thead>
                <tbody>
                  {people.map((p) => (
                    <tr key={p.login}>
                      <td style={{ ...td, fontWeight: 700 }}>{p.name || p.login}<div style={{ fontSize: '0.7rem', color: 'var(--hp-dim)', fontWeight: 400 }}>{p.login}{p.offTeam ? ' · not active' : ''}</div></td>
                      <td style={td}>{p.answer
                        ? <><span style={pill('rgba(34,197,94,0.12)', GREEN)}><CheckCircle2 size={11} /> {p.answer.cannot.length ? `${p.answer.cannot.length} can't` : 'Every night'}</span><div style={{ fontSize: '0.7rem', color: 'var(--hp-dim)', marginTop: 3 }}>{fmtStamp(p.answer.submittedAt)}{p.answer.by && p.answer.by !== p.login ? ` · by ${p.answer.by}` : ''}{p.answer.changes ? ` · changed ${p.answer.changes}×` : ''}</div></>
                        : <span style={pill(late ? 'rgba(239,68,68,0.12)' : 'rgba(245,158,11,0.14)', late ? RED : AMBER)}>Waiting</span>}</td>
                      <td style={{ ...td, color: 'var(--hp-dim)' }}>{p.answer ? (p.answer.cannot.map(fmtNight).join(', ') || '—') : ''}</td>
                      <td style={{ ...td, color: 'var(--hp-dim)' }}>{p.answer && p.answer.note}</td>
                      {!readOnly && <td style={td}><button style={{ ...ghost, padding: '5px 10px', fontSize: '0.74rem' }} onClick={() => setEditFor({ login: p.login, name: p.name, cannot: new Set(p.answer ? p.answer.cannot : []), note: p.answer ? p.answer.note : '' })}><Pencil size={12} /> Enter for them</button></td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {editFor && !readOnly && (
            <div style={{ ...card, borderColor: 'var(--hp-gold)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <strong>Nights {editFor.name || editFor.login} can't work</strong>
                <span style={{ fontSize: '0.78rem', color: 'var(--hp-dim)' }}>(e.g. told you by phone; recorded as entered by you)</span>
                <button onClick={() => setEditFor(null)} style={{ ...ghost, marginLeft: 'auto', padding: '4px 8px' }}><X size={13} /></button>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
                {nights.map((n) => {
                  const on = editFor.cannot.has(n.date);
                  return (
                    <button key={n.date} onClick={() => { const s = new Set(editFor.cannot); on ? s.delete(n.date) : s.add(n.date); setEditFor({ ...editFor, cannot: s }); }}
                      style={{ padding: '6px 10px', borderRadius: 8, border: `1px solid ${on ? RED : n.peak ? ROSE : 'var(--hp-border)'}`, background: on ? 'rgba(239,68,68,0.18)' : 'transparent', color: on ? RED : 'var(--hp-text)', fontWeight: 700, fontSize: '0.76rem', cursor: 'pointer', fontFamily: 'var(--font-body)' }}>
                      {fmtNight(n.date)}{n.peak ? ' (Sat)' : ''}
                    </button>
                  );
                })}
              </div>
              <input style={{ ...input, marginBottom: 12 }} placeholder="Note (optional)" value={editFor.note} onChange={(e) => setEditFor({ ...editFor, note: e.target.value })} />
              <button style={btn('var(--hp-gold)')} disabled={busy} onClick={saveFor}>{busy ? <Loader2 size={15} style={{ animation: 'spin 0.8s linear infinite' }} /> : <CheckCircle2 size={15} />} Save: {editFor.cannot.size ? `can't work ${editFor.cannot.size}` : 'can work every night'}</button>
            </div>
          )}
        </>
      )}
    </>
  );
}

function NightNames({ cant, answered, waiting }) {
  const cantSet = new Set(cant.map((p) => p.login));
  const can = answered.filter((p) => !cantSet.has(p.login));
  const names = (list) => list.map((p) => p.name || p.login).join(', ') || '—';
  return (
    <div style={{ display: 'grid', gap: 6, fontSize: '0.84rem' }}>
      <div><strong style={{ color: RED }}>Can't work ({cant.length}):</strong> {names(cant)}</div>
      <div><strong style={{ color: GREEN }}>Can work ({can.length}):</strong> {names(can)}</div>
      {waiting.length > 0 && <div style={{ color: 'var(--hp-dim)' }}><strong>Not answered ({waiting.length}):</strong> {names(waiting)}</div>}
    </div>
  );
}
